import { useState, useEffect } from "react";
import { ViewType } from "./types/navigation";
import { LoginForm } from "./components/auth/LoginForm";
import { ForgotPasswordForm } from "./components/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "./components/auth/ResetPasswordForm";
import { ChangePasswordModal } from "./components/auth/ChangePasswordModal";
import { AppLayout } from "./components/layout/AppLayout";
import { AppRouter } from "./components/layout/AppRouter";
import { useTimeBasedTheme } from "./hooks/useTimeBasedTheme";
import { useAuthSession } from "./hooks/useAuthSession";
import { useAppData } from "./hooks/useAppData";

export default function App() {
  const [isChangePasswordDismissed, setIsChangePasswordDismissed] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    if (typeof window === "undefined") return "dashboard";
    const hash = (window.location.hash || "").slice(1);
    return (hash as ViewType) || "dashboard";
  });

  const {
    currentUser,
    authView,
    resetToken,
    handleLogin: authLogin,
    handleLogout,
    handleUpdateCurrentUser: authUpdateCurrentUser,
    handleShowForgotPassword,
    handleBackToMain,
  } = useAuthSession(() => {
    loadDataFromDatabase();
  });

  const {
    tasks,
    users,
    projects,
    workLogs,
    materials,
    setUsers,
    loadDataFromDatabase,
    handleCreateProject,
    handleUpdateProject,
    handleDeleteProject,
    handleAssignFabricator,
    handleBroadcastFabricators,
    handleAcceptAssignment,
    handleDeclineAssignment,
    handleUpdateTaskStatus,
    handleCreateTask,
    handleUpdateTask,
    handleDeleteTask,
    handleAddWorkLog,
    handleUpdateWorkLog,
    handleDeleteWorkLog,
    handleAddMaterial,
    handleUpdateMaterial,
    handleDeleteMaterial,
    handleAddUser,
  } = useAppData(currentUser);

  useEffect(() => {
    setIsChangePasswordDismissed(false);
  }, [currentUser?.id]);

  const shouldPromptChangePassword = Boolean(
    currentUser &&
      (currentUser.mustChangePassword || currentUser.must_change_password) &&
      !isChangePasswordDismissed
  );

  // Time-based theme configuration
  const themeStorageKey = currentUser
    ? `theme-override-${currentUser.role}`
    : "theme-override-guest";
  const { isTransitioning, setTheme, getCurrentTheme } = useTimeBasedTheme({
    storageKey: themeStorageKey,
  });

  // Login handler
  const handleLogin = (user: typeof currentUser extends null ? never : any) => {
    authLogin(user);
    setCurrentView("dashboard");
  };

  const handleUpdateCurrentUser = (
    updatedUser: typeof currentUser extends null ? never : any
  ) => {
    authUpdateCurrentUser(updatedUser);
    handleAddUser(updatedUser);
  };

  // Role-based view guards
  useEffect(() => {
    if (!currentUser) return;

    const allowedByRole: Record<string, ReadonlyArray<ViewType>> = {
      admin: [
        "dashboard",
        "projects",
        "tasks",
        "users",
        "materials",
        "reports",
        "settings",
        "team",
        "revenue",
        "assignments",
        "archives",
        "activity-logs",
        "billing",
        "feature-bank",
      ],
      supervisor: [
        "dashboard",
        "projects",
        "tasks",
        "team",
        "reports",
        "archives",
        "feature-bank",
      ],
      fabricator: [
        "dashboard",
        "projects",
        "assignments",
        "worklog",
        "materials",
        "tasks",
        "settings",
        "feature-bank",
      ],
      client: ["dashboard", "project-status", "documentation", "billing"],
    };

    const allowed = allowedByRole[currentUser.role] || ["dashboard"];
    if (!allowed.includes(currentView)) {
      setCurrentView("dashboard");
      try {
        window.location.hash = "dashboard";
      } catch {}
    }
  }, [currentUser, currentView]);

  // URL Hash navigation listener
  useEffect(() => {
    const validViews: ViewType[] = [
      "dashboard",
      "projects",
      "tasks",
      "users",
      "materials",
      "reports",
      "settings",
      "team",
      "worklog",
      "revenue",
      "assignments",
      "archives",
      "project-status",
      "documentation",
      "admin-projects",
      "admin-tasks",
      "activity-logs",
      "billing",
      "feature-bank",
    ];

    const handleHashChange = () => {
      const rawHash = window.location.hash.slice(1);
      if (rawHash === "admin-projects" || rawHash === "admin-tasks") {
        window.location.hash = "projects";
        setCurrentView("projects");
        try {
          localStorage.setItem("currentView", "projects");
        } catch {}
        return;
      }
      if (rawHash === "assignments" && currentUser?.role === "fabricator") {
        window.location.hash = "projects";
        setCurrentView("projects");
        try {
          localStorage.setItem("currentView", "projects");
        } catch {}
        return;
      }
      if (validViews.includes(rawHash as ViewType)) {
        setCurrentView(rawHash as ViewType);
        try {
          localStorage.setItem("currentView", rawHash);
        } catch {}
      } else if (!rawHash) {
        setCurrentView("dashboard");
        try {
          localStorage.setItem("currentView", "dashboard");
        } catch {}
      }
    };

    window.addEventListener("hashchange", handleHashChange);

    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a[href^="#"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const raw = anchor.getAttribute("href") || "";
      const view = raw.startsWith("#") ? raw.slice(1) : raw;
      if (validViews.includes(view as ViewType)) {
        setCurrentView(view as ViewType);
      }
    };
    document.addEventListener("click", handleAnchorClick);

    const rawHash = window.location.hash.slice(1);
    if (validViews.includes(rawHash as ViewType)) {
      handleHashChange();
    } else {
      try {
        const storedUser = localStorage.getItem("currentUser");
        const storedView = localStorage.getItem("currentView") as ViewType | null;
        if (storedUser) {
          const view =
            storedView && validViews.includes(storedView)
              ? storedView
              : "dashboard";
          setCurrentView(view);
          if (!rawHash) {
            try {
              window.location.hash = view;
            } catch {}
          }
        }
      } catch {}
    }

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      document.removeEventListener("click", handleAnchorClick);
    };
  }, [currentUser]);

  if (!currentUser) {
    return (
      <>
        {authView === "forgot-password" && (
          <ForgotPasswordForm onBackToLogin={handleBackToMain} />
        )}
        {authView === "reset-password" && (
          <ResetPasswordForm
            token={resetToken}
            onBackToLogin={handleBackToMain}
          />
        )}
        {authView === "main" && (
          <LoginForm
            onLogin={handleLogin}
            onShowForgotPassword={handleShowForgotPassword}
          />
        )}
      </>
    );
  }

  return (
    <>
      <AppLayout
      currentUser={currentUser}
      onLogout={handleLogout}
      currentTheme={getCurrentTheme()}
      onThemeChange={setTheme}
      isTransitioning={isTransitioning}
    >
      <AppRouter
        currentUser={currentUser}
        currentView={currentView}
        projects={projects}
        tasks={tasks}
        users={users}
        workLogs={workLogs}
        materials={materials}
        setUsers={setUsers}
        onCreateProject={
          currentUser.role === "admin" || currentUser.role === "supervisor"
            ? handleCreateProject
            : undefined
        }
        onUpdateProject={handleUpdateProject}
        onDeleteProject={handleDeleteProject}
        onAssignFabricator={
          currentUser.role === "supervisor"
            ? handleAssignFabricator
            : undefined
        }
        onBroadcastFabricators={
          currentUser.role === "supervisor"
            ? handleBroadcastFabricators
            : undefined
        }
        onAcceptAssignment={handleAcceptAssignment}
        onDeclineAssignment={handleDeclineAssignment}
        onCreateTask={handleCreateTask}
        onUpdateTask={handleUpdateTask}
        onUpdateTaskStatus={handleUpdateTaskStatus}
        onDeleteTask={handleDeleteTask}
        onAddWorkLog={handleAddWorkLog}
        onUpdateWorkLog={handleUpdateWorkLog}
        onDeleteWorkLog={handleDeleteWorkLog}
        onAddMaterial={handleAddMaterial}
        onUpdateMaterial={handleUpdateMaterial}
        onDeleteMaterial={handleDeleteMaterial}
        onAddUser={handleAddUser}
        onUpdateCurrentUser={handleUpdateCurrentUser}
      />
    </AppLayout>
    <ChangePasswordModal
      isOpen={shouldPromptChangePassword}
      currentUser={currentUser}
      onClose={() => setIsChangePasswordDismissed(true)}
      onSuccess={(updatedUser) => {
        handleUpdateCurrentUser(updatedUser);
        setIsChangePasswordDismissed(true);
      }}
    />
    </>
  );
}
